import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import type { Fanpage } from '../../shared/types';

interface FanpageCardProps {
  fanpage: Fanpage;
}

function formatNumber(n: number): string {
  return n.toLocaleString('vi-VN');
}

export const FanpageCard: React.FC<FanpageCardProps> = ({ fanpage }) => {
  const { name, link, follow, price, status, info_1, note } = fanpage;
  const [imgError, setImgError] = React.useState(false);
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [zoom, setZoom] = React.useState(1);

  const statusLabel = status === 'còn' ? 'còn' : status === 'hết' ? 'hết' : '';
  const statusColor = status === 'còn' ? 'success' : status === 'hết' ? 'error' : 'default';
  const hasImage = !imgError && (info_1.startsWith('http://') || info_1.startsWith('https://'));
  const ariaLabel = `Fanpage: ${name}, tình trạng: ${statusLabel || 'không rõ'}`;

  const handleZoomIn = () => setZoom(z => Math.min(z + 0.5, 4));
  const handleZoomOut = () => setZoom(z => Math.max(z - 0.5, 0.5));
  const handleClose = () => { setLightboxOpen(false); setZoom(1); };

  return (
    <>
      <Card aria-label={ariaLabel} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Image area */}
        {hasImage && (
          <Box
            onClick={() => setLightboxOpen(true)}
            sx={{
              cursor: 'zoom-in',
              overflow: 'hidden',
              bgcolor: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 160,
              maxHeight: 220,
            }}
          >
            <Box
              component="img"
              src={info_1}
              alt={`Ảnh đại diện của fanpage ${name}`}
              onError={() => setImgError(true)}
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: 220,
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </Box>
        )}

        <CardContent sx={{ flexGrow: 1 }}>
          {link ? (
            <Link
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              color="primary"
              variant="h6"
              component="a"
              aria-label={`${name} (mở trong tab mới)`}
              sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}
            >
              {name}
            </Link>
          ) : (
            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>{name}</Typography>
          )}

          {statusLabel && (
            <Chip label={statusLabel} color={statusColor as 'success' | 'error'} size="small" sx={{ mb: 1 }} />
          )}

          {note && (
            <Tooltip title={note} arrow placement="top">
              <Chip
                label="ℹ Ghi chú"
                color="warning"
                size="small"
                sx={{ mb: 1, ml: statusLabel ? 1 : 0, cursor: 'pointer' }}
              />
            </Tooltip>
          )}

          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Follow: <strong>{formatNumber(follow)}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Giá: <strong>{formatNumber(price)} đ</strong>
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Lightbox */}
      <Modal open={lightboxOpen} onClose={handleClose} aria-label="Xem ảnh phóng to">
        <Box
          onClick={handleClose}
          sx={{
            position: 'fixed', inset: 0,
            bgcolor: 'rgba(0,0,0,0.9)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            zIndex: 1300,
          }}
        >
          {/* Controls */}
          <Box
            onClick={e => e.stopPropagation()}
            sx={{ display: 'flex', gap: 1, mb: 1, zIndex: 1 }}
          >
            <IconButton onClick={handleZoomOut} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
              <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>−</span>
            </IconButton>
            <Typography sx={{ color: 'white', alignSelf: 'center', minWidth: 40, textAlign: 'center' }}>
              {Math.round(zoom * 100)}%
            </Typography>
            <IconButton onClick={handleZoomIn} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
              <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>+</span>
            </IconButton>
            <IconButton onClick={handleClose} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
              <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>✕</span>
            </IconButton>
          </Box>

          {/* Image */}
          <Box
            onClick={e => e.stopPropagation()}
            sx={{ overflow: 'auto', maxWidth: '95vw', maxHeight: '85vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Box
              component="img"
              src={info_1}
              alt={`Ảnh phóng to của fanpage ${name}`}
              sx={{
                transform: `scale(${zoom})`,
                transformOrigin: 'center center',
                transition: 'transform 0.2s',
                maxWidth: '90vw',
                maxHeight: '80vh',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </Box>

          <Typography sx={{ color: 'rgba(255,255,255,0.6)', mt: 1, fontSize: '0.75rem' }}>
            Bấm ra ngoài để đóng
          </Typography>
        </Box>
      </Modal>
    </>
  );
};
